"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRightIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, SearchInput, useIsMobile } from "@saridana-core/react-ui-kit";
import { TFaq } from "@shared/types";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { useMemo, useState } from "react";
import { searchCategories } from "../constants";
import { fetchArticles, fetchFaqs, fetchProducts, normalizeArticles, normalizeFaqs, normalizeProducts } from "../lib";
import { SearchCategory } from "../types";
import { FaqSearchDialog } from "./faq-search-dialog";
import { HighlightedText } from "./highlighted-text";
import { SearchCategoryIcon } from "./search-category-icon";
function SearchItem(t0) {
  const $ = _c(14);
  const {
    category,
    title,
    keyword
  } = t0;
  let t1;
  if ($[0] !== category) {
    t1 = <SearchCategoryIcon category={category} size="md" />;
    $[0] = category;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = searchCategories[category as SearchCategory];
  let t3;
  if ($[2] !== t2) {
    t3 = <span>{t2}</span>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArrowRightIcon className="size-5 mx-2 inline-block" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== keyword || $[6] !== title) {
    t5 = keyword ? <HighlightedText keyword={keyword} text={title} /> : title;
    $[5] = keyword;
    $[6] = title;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t5) {
    t6 = <div className="flex-1">{t3}{t4}{t5}</div>;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t1 || $[12] !== t6) {
    t7 = <>{t1}{t6}</>;
    $[11] = t1;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
export function SearchDialog(t0) {
  const $ = _c(75);
  const {
    open,
    onOpenChange,
    onMoreClick
  } = t0;
  const isMobile = useIsMobile();
  const [keyword, setKeyword] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = val => {
      setKeyword(val);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleSearch = t1;
  let t2;
  if ($[1] !== open) {
    t2 = {
      initialPageParam: 0,
      getNextPageParam: _temp,
      enabled: open,
      keepPreviousData: false
    };
    $[1] = open;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const options = t2;
  let t3;
  let t4;
  if ($[3] !== keyword) {
    t3 = ["search-articles", keyword];
    t4 = t5 => {
      const {
        pageParam
      } = t5;
      return fetchArticles({
        pageParam,
        keyword
      });
    };
    $[3] = keyword;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== options || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      ...options
    };
    $[6] = options;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    data: articles,
    isFetchingNextPage: isArticlesFetchingNextPage,
    isFetching: isArticlesFetching
  } = useInfiniteQuery(t5);
  let t6;
  let t7;
  if ($[10] !== keyword) {
    t6 = ["search-products", keyword];
    t7 = t8 => {
      const {
        pageParam: pageParam_0
      } = t8;
      return fetchProducts({
        pageParam: pageParam_0,
        keyword
      });
    };
    $[10] = keyword;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  let t8;
  if ($[13] !== options || $[14] !== t6 || $[15] !== t7) {
    t8 = {
      queryKey: t6,
      queryFn: t7,
      ...options
    };
    $[13] = options;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const {
    data: products,
    isFetchingNextPage: isProductsFetchingNextPage,
    isFetching: isProductsFetching
  } = useInfiniteQuery(t8);
  let t10;
  let t9;
  if ($[17] !== keyword) {
    t9 = ["search-faqs", keyword];
    t10 = t11 => {
      const {
        pageParam: pageParam_1
      } = t11;
      return fetchFaqs({
        pageParam: pageParam_1,
        keyword
      });
    };
    $[17] = keyword;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[20] !== options || $[21] !== t10 || $[22] !== t9) {
    t11 = {
      queryKey: t9,
      queryFn: t10,
      ...options
    };
    $[20] = options;
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const {
    data: faqs,
    isFetchingNextPage: isFaqsFetchingNextPage,
    isFetching: isFaqsFetching
  } = useInfiniteQuery(t11);
  let t12;
  if ($[24] !== articles || $[25] !== faqs || $[26] !== products) {
    const list = [];
    const pushPages = (queryData, normalizer) => {
      queryData?.pages?.forEach(p => {
        if (p?.data) {
          list.push(...normalizer(p.data));
        }
      });
    };
    pushPages(articles, normalizeArticles);
    pushPages(products, normalizeProducts);
    pushPages(faqs, normalizeFaqs);
    t12 = list.sort(_temp2);
    $[24] = articles;
    $[25] = faqs;
    $[26] = products;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const combinedResults = t12;
  const isFetching = isArticlesFetching || isArticlesFetchingNextPage || isFaqsFetching || isFaqsFetchingNextPage || isProductsFetching || isProductsFetchingNextPage;
  const [selectedFaq, setSelectedFaq] = useState(null);
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = item => {
      if (item && item.raw && item.category === "faq") {
        setSelectedFaq(item.raw);
      }
    };
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const handleOpenFaq = t13;
  const queryClient = useQueryClient();
  let t14;
  if ($[29] !== onOpenChange || $[30] !== queryClient) {
    t14 = async open_0 => {
      if (!open_0) {
        await queryClient.cancelQueries();
        queryClient.removeQueries({
          queryKey: ["search-articles"]
        });
        queryClient.removeQueries({
          queryKey: ["search-products"]
        });
        queryClient.removeQueries({
          queryKey: ["search-faqs"]
        });
        setKeyword("");
      }
      if (onOpenChange) {
        onOpenChange(open_0);
      }
    };
    $[29] = onOpenChange;
    $[30] = queryClient;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const handleOpenChange = t14;
  let t15;
  if ($[32] !== keyword || $[33] !== onMoreClick) {
    t15 = () => {
      onMoreClick(keyword);
    };
    $[32] = keyword;
    $[33] = onMoreClick;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const handleMoreClick = t15;
  const t16 = !isMobile;
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <DialogHeader><DialogTitle className="hidden text-left">Cari sesuatu tentang saridana</DialogTitle></DialogHeader>;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="absolute inline-flex items-center justify-center h-full px-4"><MagnifyingGlassIcon className="size-6" /></div>;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== keyword) {
    t19 = <div className="mt-4 md:-mt-4 sticky bg-white top-4"><SearchInput value={keyword} onSearch={handleSearch} appendChild={t18} buttonClassName="hidden" inputClassName="pl-14 rounded-xl py-7 relative font-medium text-lg border-gray-900" placeholder="Cari..." /></div>;
    $[37] = keyword;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== isFetching) {
    t20 = isFetching && <div className="flex items-center justify-center text-center"><span className="w-8 h-8 rounded-full border-t-2 border-l-2 border-r-2  border-primary inline-block animate-spin" /></div>;
    $[39] = isFetching;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== combinedResults || $[42] !== keyword) {
    t21 = !combinedResults || combinedResults && combinedResults.length <= 0 && <div className="flex items-center justify-start text-left"><p className="text-gray-700">Tidak ada hasil yang sesuai dengan "{keyword}".</p></div>;
    $[41] = combinedResults;
    $[42] = keyword;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== combinedResults || $[45] !== keyword || $[46] !== onOpenChange) {
    let t23;
    if ($[48] !== keyword || $[49] !== onOpenChange) {
      t23 = (item_0, index) => {
        if (item_0.url) {
          return <Link href={item_0.url} className="flex items-center gap-2.5 py-2 px-4 hover:bg-primary-50 rounded-lg" key={item_0.category + "-" + item_0.title + "-" + index} onClick={() => onOpenChange ? onOpenChange(false) : null}><SearchItem title={item_0.title} category={item_0.category} keyword={keyword} /></Link>;
        }
        return <a onClick={() => handleOpenFaq(item_0)} className="flex items-center gap-2.5 py-2 px-4 cursor-pointer hover:bg-primary-50 rounded-lg" key={item_0.category + "-" + item_0.title + "-" + index}><SearchItem title={item_0.title} category={item_0.category} keyword={keyword} /></a>;
      };
      $[48] = keyword;
      $[49] = onOpenChange;
      $[50] = t23;
    } else {
      t23 = $[50];
    }
    t22 = combinedResults.map(t23);
    $[44] = combinedResults;
    $[45] = keyword;
    $[46] = onOpenChange;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[51] !== t20 || $[52] !== t21 || $[53] !== t22) {
    t23 = <div className="mt-4 space-y-2">{t20}{t21}{t22}</div>;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== combinedResults || $[56] !== handleMoreClick) {
    t24 = combinedResults && combinedResults.length > 0 && <><hr className="border-gray-100 mt-4" /><Button className="w-full text-center text-primary-600 -mb-2" variant="link" onClick={handleMoreClick}>Lihat Semua Hasil</Button></>;
    $[55] = combinedResults;
    $[56] = handleMoreClick;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t16 || $[59] !== t19 || $[60] !== t23 || $[61] !== t24) {
    t25 = <DialogContent className="max-w-full w-[100vw] md:max-w-[60vw] p-6 !rounded-2xl" hideCloseButton={t16} overlayClassName="flex justify-center items-start xl:mt-[90px] bg-black/60">{t17}{t19}{t23}{t24}</DialogContent>;
    $[58] = t16;
    $[59] = t19;
    $[60] = t23;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== handleOpenChange || $[64] !== open || $[65] !== t25) {
    t26 = <Dialog defaultOpen={open} open={open} onOpenChange={handleOpenChange}>{t25}</Dialog>;
    $[63] = handleOpenChange;
    $[64] = open;
    $[65] = t25;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== selectedFaq) {
    t27 = open_1 => setSelectedFaq(!open_1 ? null : selectedFaq);
    $[67] = selectedFaq;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== selectedFaq || $[70] !== t27) {
    t28 = <FaqSearchDialog faq={selectedFaq} onOpenChange={t27} />;
    $[69] = selectedFaq;
    $[70] = t27;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  let t29;
  if ($[72] !== t26 || $[73] !== t28) {
    t29 = <>{t26}{t28}</>;
    $[72] = t26;
    $[73] = t28;
    $[74] = t29;
  } else {
    t29 = $[74];
  }
  return t29;
}
function _temp2(a, b) {
  return a.title.localeCompare(b.title);
}
function _temp(lastPage) {
  const meta = lastPage.meta.pagination;
  const next = meta.start + meta.limit;
  return next < meta.total ? next : undefined;
}